import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import lodashIsString from 'lodash/isString';
import { translationPropType } from '@eb/i18n';
import { Button } from '@eb/eds-button';
import { ListItem } from '@eb/eds-list-item';
import { VERTICAL_ALIGNMENTS_PROP_TYPE } from '@eb/eds-list-item';
import { MoreActions } from '@eb/eds-more-actions';
import { TEXT_ITEMS_PROP_TYPE } from '@eb/eds-text-list';
import {
    ICON_TYPE_PROP_TYPE,
    ICON_SIZE_PROP_TYPE,
    COLOR_PROP_TYPE,
} from '@eb/eds-icon';

import './EditListItem.scss';

const _getMoreActions = (
    moreActionItems,
    onAction,
    isMoreActionDisabled = false,
) => {
    // Remove component if items are not provided
    if (!moreActionItems) {
        return null;
    }

    const moreActionsClass = classNames(
        'eds-g-cell',
        'eds-g-cell--has-overflow',
        'eds-g-cell-1-12',
    );

    let moreActions;

    // Leave wrapper but exclude component when items are empty
    if (!isEmpty(moreActionItems)) {
        moreActions = (
            <MoreActions
                items={moreActionItems}
                onAction={onAction}
                align="right"
                isDisabled={isMoreActionDisabled}
            />
        );
    }

    return (
        <div
            className={moreActionsClass}
            data-spec="edit-list-item-more-actions-bar"
        >
            <div className="eds-text--right">{moreActions}</div>
        </div>
    );
};

export default class EditListItem extends PureComponent {
    static propTypes = {
        /**
         * Action items
         */
        actionItems: TEXT_ITEMS_PROP_TYPE,
        /**
         * Any child nodes to be displayed that should not have the content styling
         */
        children: PropTypes.node,
        /**
         * The primary content to be displayed
         */
        content: PropTypes.node,
        /**
         * Generic content for the left side
         */
        extraContent: PropTypes.node,
        /**
         * Icon to include in the list item that when specified will wrap the `children` to include the icon
         */
        iconType: ICON_TYPE_PROP_TYPE,
        /**
         * Size of included icon
         */
        iconSize: ICON_SIZE_PROP_TYPE,
        /**
         * Color of included icon
         */
        iconColor: COLOR_PROP_TYPE,
        /**
         * URL of image to be displayed with list item content
         */
        imageUrl: PropTypes.string,
        /**
         * Alt text for image to be displayed with list item content
         */
        imageAlt: translationPropType,
        /**
         * Option for image in list item to be square shaped
         */
        isSquareImage: PropTypes.bool,
        /**
         * Callback function that will be called when a action item is selected
         * (value, index) => {}
         **/
        onAction: PropTypes.func,
        /**
         * State of card being selected
         **/
        isSelected: PropTypes.bool,
        /**
         * The secondary content to be displayed
         */
        secondaryContent: PropTypes.node,
        /**
         * The tertiary content to be displayed
         */
        tertiaryContent: PropTypes.node,
        /**
         * Verical Alignment on item
         */
        verticalAlignment: VERTICAL_ALIGNMENTS_PROP_TYPE,
        /**
         * Whether or not the text list item is tenatively selected.
         * This comes into play on hover or via arrow key press before an
         * actual selection is made
         */
        isTentativelySelected: PropTypes.bool,
        /**
         * Whether or not the item should use an alternate theme
         */
        useAltTheme: PropTypes.bool,
        /**
         * Whether or not the item is disabled
         */
        isDisabled: PropTypes.bool,
        /**
         * Handler of clicking on content
         */
        onClick: PropTypes.func,
        /**
         * URL to link content to
         */
        href: PropTypes.string,
        /**
         * An option to prevent column wrapping (preserve columns) on all sizes of
         * small screens if actionItems are present.
         */
        noWrap: PropTypes.bool,
        /**
         * Custom container class
         */
        __containerClassName: PropTypes.string,
        /**
         * Whether or not the more action options are disabled
         */
        isMoreActionDisabled: PropTypes.bool,
    };

    static defaultProps = {
        noWrap: false,
        isMoreActionDisabled: false,
    };

    getMainContent = () => {
        const {
            content,
            secondaryContent,
            tertiaryContent,
            noWrap,
            actionItems,
            extraContent,
            iconType,
            iconSize,
            iconColor,
            imageUrl,
            imageAlt,
            isSquareImage,
            isSelected,
            isTentativelySelected,
            useAltTheme,
            isDisabled,
            verticalAlignment,
            children,
        } = this.props;
        const contentClass = classNames(
            'eds-list-item-content',
            'eds-align--left',
            'eds-align--mn-right',
            'eds-align--md-right',
            'eds-align--mw-right',
            'eds-align--ln-right',
            'eds-align--lg-right',
            'eds-align--lw-right',
        );
        const contentBarGridClass = classNames(
            actionItems && !noWrap ? 'eds-g-cell-12-12' : 'eds-g-cell-7-12',
            'eds-g-cell-mn-7-12',
            'eds-g-cell-md-7-12',
            'eds-g-cell-mw-7-12',
            'eds-g-cell-ln-7-12',
            'eds-g-cell-lg-7-12',
            'eds-g-cell-lw-7-12',
        );

        const contentBarClass = classNames(
            'edit-list-item__content-bar',
            'eds-text-bs',
            'eds-text-color--ui-600',
            'eds-l-sn-mar-bot-1',
            'eds-l-sm-mar-bot-1',
            'eds-l-sw-mar-bot-1',
        );

        const actionsBarGridClass = classNames(
            actionItems && !noWrap ? 'eds-g-cell-12-12' : 'eds-g-cell-5-12',
            'eds-g-cell-mn-5-12',
            'eds-g-cell-md-5-12',
            'eds-g-cell-mw-5-12',
            'eds-g-cell-ln-5-12',
            'eds-g-cell-lg-5-12',
            'eds-g-cell-lw-5-12',
        );

        const actionsBarClass = classNames(
            'edit-list-item__action-bar',
            actionItems ? 'edit-list-item--column' : 'edit-list-item--row',
            'eds-align--left',
            'eds-align--mn-right',
            'eds-align--md-right',
            'eds-align--mw-right',
            'eds-align--ln-right',
            'eds-align--lg-right',
            'eds-align--lw-right',
            'eds-text-bm',
            'eds-text-color--ui-900',
        );
        let primaryContent = content;

        // Customize primary content display when string is passed
        if (lodashIsString(content)) {
            primaryContent = (
                <p className="eds-text-bm eds-text-color--ui-900">{content}</p>
            );
        }

        return (
            <div className="eds-g-group">
                <div className={contentClass}>
                    <div className={contentBarGridClass}>
                        <div
                            className={contentBarClass}
                            data-spec="edit-list-item-content-bar"
                        >
                            <ListItem
                                data-spec="edit-list-item"
                                extraContent={extraContent}
                                iconType={iconType}
                                iconSize={iconSize}
                                iconColor={iconColor}
                                imageUrl={imageUrl}
                                imageAlt={imageAlt}
                                isSquareImage={isSquareImage}
                                isSelected={isSelected}
                                isTentativelySelected={isTentativelySelected}
                                useAltTheme={useAltTheme}
                                isDisabled={isDisabled}
                                verticalAlignment={verticalAlignment}
                            >
                                {primaryContent}
                                {secondaryContent}
                                {tertiaryContent}
                            </ListItem>
                        </div>
                    </div>
                    <div className={actionsBarGridClass}>
                        <div
                            className={actionsBarClass}
                            data-spec="edit-list-item-action-bar"
                        >
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const {
            actionItems,
            onAction,
            isTentativelySelected,
            isSelected,
            useAltTheme,
            isDisabled,
            verticalAlignment,
            onClick,
            href,
            __containerClassName,
            isMoreActionDisabled,
        } = this.props;

        const editCardClassNames = classNames(
            'eds-g-group',
            'eds-list-item',
            'edit-list-item',
            'eds-align--space-between',
            'eds-l-pad-all-4',
            'eds-l-mn-pad-hor-6',
            'eds-l-md-pad-hor-6',
            'eds-l-mw-pad-hor-6',
            'eds-l-ln-pad-hor-6',
            'eds-l-lg-pad-hor-6',
            'eds-l-lw-pad-hor-6',
            __containerClassName,
            {
                'eds-list-item--selected': isSelected,
                'eds-list-item--tentatively-selected': isTentativelySelected,
                'eds-list-item--alt-theme': useAltTheme,
                'eds-list-item--disabled': isDisabled,
                [`eds-list-item--align-${verticalAlignment}`]: verticalAlignment,
            },
        );

        const contentGridClass = classNames(
            actionItems && !onClick ? 'eds-g-cell-11-12' : 'eds-g-cell-12-12',
        );

        let mainContent = this.getMainContent();

        const shouldWrapContentWithButton = !isDisabled && (onClick || href);

        if (shouldWrapContentWithButton) {
            const buttonType = href ? 'link' : 'button';

            mainContent = (
                <Button
                    type={buttonType}
                    href={href}
                    onClick={onClick}
                    style="none"
                    size="block"
                    data-spec="edit-list-item-button"
                >
                    {mainContent}
                </Button>
            );
        }

        return (
            <div className={editCardClassNames} data-spec="edit-list-item">
                <div className={contentGridClass}>{mainContent}</div>
                {_getMoreActions(actionItems, onAction, isMoreActionDisabled)}
            </div>
        );
    }
}
